export class LRUObjectCache<K, V> {
  private readonly cache: Map<K, V> = new Map();
  private readonly queue: K[] = [];
  public hits: number = 0;
  public misses: number = 0;

  constructor(
    private readonly size: number,
    private readonly compareKeys: (a: K, b: K) => boolean,
    private readonly dispose: (value: V) => void = () => {},
  ) {}

  has(key: K): boolean {
    return !!this.get(key);
  }

  add(key: K, value: V) {
    if (this.cache.has(key)) {
      this.delete(key);
    }

    this.cache.set(key, value);
    this.queue.push(key);
    if (this.queue.length > this.size) {
      const keyToRemove = this.queue.shift();
      if (keyToRemove) {
        const removedValue = this.cache.get(keyToRemove);
        if (removedValue) {
          this.dispose(removedValue);
        }
        this.cache.delete(keyToRemove);
      }
    }
  }

  get(key: K): V | undefined {
    for (const k of this.queue) {
      if (this.compareKeys(k, key)) {
        const entry = this.cache.get(k);
        if (entry) {
          // move to top of queue
          this.queue.push(this.queue.splice(this.queue.indexOf(k), 1)[0]);
        }
        this.hits++;
        return entry;
      }
    }
    this.misses++;
    return undefined;
  }

  delete(key: K) {
    const value = this.cache.get(key);
    if (value) {
      this.dispose(value);
    }
    this.cache.delete(key);
    const index = this.queue.findIndex((k) => this.compareKeys(k, key));
    if (index !== -1) {
      this.queue.splice(index, 1);
    }
  }

  clear() {
    for (const key of this.cache.keys()) {
      this.delete(key);
    }
  }

  /** @deprecated Only to be used for special cases like when disposing an item is async */
  getAllValues(): V[] {
    return [...this.cache.values()];
  }
}
